import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  AppointmentReview,
  AppointmentReviewSummary,
  getAppointmentReviewSummary,
  listAppointmentReviews,
  replyToAppointmentReview,
} from "../../../../services/appointmentReviewService";
import { getProfessionals, Professional } from "../../../../services/professionalService";

const ReviewsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const weeklyAlertRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<AppointmentReview[]>([]);
  const [summary, setSummary] = useState<AppointmentReviewSummary | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [ratingFilter, setRatingFilter] = useState("");
  const [professionalFilter, setProfessionalFilter] = useState("");
  const [hasCommentFilter, setHasCommentFilter] = useState("");
  const [hasReplyFilter, setHasReplyFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyingId, setReplyingId] = useState<number | null>(null);
  const [highlightWeeklyAlert, setHighlightWeeklyAlert] = useState(false);

  useEffect(() => {
    document.title = t("studio.reviews.pageTitle");
  }, [t]);

  useEffect(() => {
    void getProfessionals({ per_page: 100 })
      .then((response) => setProfessionals(response.data ?? []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (searchParams.get("focus") !== "weekly_alert") {
      return;
    }

    setHighlightWeeklyAlert(true);
    setHasReplyFilter("0");

    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!highlightWeeklyAlert || !summary?.weekly_alert || loading) {
      return;
    }

    weeklyAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [highlightWeeklyAlert, loading, summary?.weekly_alert]);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        rating: ratingFilter ? Number(ratingFilter) : null,
        professional_id: professionalFilter ? Number(professionalFilter) : null,
        has_comment:
          hasCommentFilter === "1" ? true : hasCommentFilter === "0" ? false : null,
        has_reply:
          hasReplyFilter === "1" ? true : hasReplyFilter === "0" ? false : null,
        from: from || null,
        to: to || null,
        per_page: 50,
      };

      const [listResponse, summaryResponse] = await Promise.all([
        listAppointmentReviews(params),
        getAppointmentReviewSummary({
          professional_id: params.professional_id,
          from: params.from,
          to: params.to,
        }),
      ]);

      setReviews(listResponse.data ?? []);
      setSummary(summaryResponse.data ?? null);
    } catch {
      toast.error(t("studio.reviews.toast.loadError"));
      setReviews([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [
    from,
    hasCommentFilter,
    hasReplyFilter,
    professionalFilter,
    ratingFilter,
    t,
    to,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const renderStars = (rating: number | null) => {
    if (rating === null) {
      return "—";
    }

    return (
      <span className="text-warning" aria-label={t("studio.reviews.ratingLabel", { count: rating })}>
        {"★".repeat(rating)}
        <span className="text-muted">{"★".repeat(5 - rating)}</span>
      </span>
    );
  };

  const handleReply = async (review: AppointmentReview) => {
    const draft = (replyDrafts[review.id] ?? "").trim();

    if (!draft) {
      toast.error(t("studio.reviews.reply.empty"));
      return;
    }

    setReplyingId(review.id);

    try {
      const response = await replyToAppointmentReview(review.id, draft);
      const updated = response.data;

      if (updated) {
        setReviews((current) =>
          current.map((item) => (item.id === review.id ? { ...item, ...updated } : item))
        );
        setReplyDrafts((current) => {
          const next = { ...current };
          delete next[review.id];
          return next;
        });
      }

      toast.success(t("studio.reviews.reply.success"));
    } catch {
      toast.error(t("studio.reviews.reply.error"));
    } finally {
      setReplyingId(null);
    }
  };

  const weeklyAlert = summary?.weekly_alert ?? null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("studio.reviews.title")} pageTitle={t("menu.reviews")} />
        <ToastContainer />

        {weeklyAlert ? (
          <div ref={weeklyAlertRef}>
            <Alert
              color="warning"
              className={`mb-3 ${highlightWeeklyAlert ? "border border-warning border-2 shadow-sm" : ""}`}
            >
              <strong className="d-block mb-1">{t("studio.reviews.weeklyAlert.title")}</strong>
              <span>
                {t("studio.reviews.weeklyAlert.body", {
                  previous: Number(weeklyAlert.previous_average).toFixed(1),
                  current: Number(weeklyAlert.current_average).toFixed(1),
                })}
              </span>
              {highlightWeeklyAlert ? (
                <p className="small mb-0 mt-2">
                  {t("studio.reviews.weeklyAlert.focusHint")}
                </p>
              ) : null}
            </Alert>
          </div>
        ) : null}

        <Row className="mb-3">
          <Col xl={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <p className="text-muted mb-1">{t("studio.reviews.summary.average")}</p>
                <h3 className="mb-0">
                  {summary?.average != null ? summary.average.toFixed(1) : "—"}
                  {summary?.average != null && (
                    <small className="text-muted fs-6 ms-1">/ 5</small>
                  )}
                </h3>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <p className="text-muted mb-1">{t("studio.reviews.summary.count")}</p>
                <h3 className="mb-0">{summary?.count ?? 0}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col xl={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <p className="text-muted mb-2">{t("studio.reviews.summary.distribution")}</p>
                <div className="d-flex flex-wrap gap-2">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <Badge key={star} color="light" className="text-body border">
                      {star}★ · {summary?.distribution?.[star] ?? 0}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card>
          <CardHeader className="border-0">
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.rating")}</Label>
                <Input
                  type="select"
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                >
                  <option value="">{t("studio.reviews.filters.allRatings")}</option>
                  {[5, 4, 3, 2, 1].map((star) => (
                    <option key={star} value={star}>
                      {star} ★
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.professional")}</Label>
                <Input
                  type="select"
                  value={professionalFilter}
                  onChange={(e) => setProfessionalFilter(e.target.value)}
                >
                  <option value="">{t("studio.reviews.filters.allProfessionals")}</option>
                  {professionals.map((pro) => (
                    <option key={pro.id} value={pro.id}>
                      {pro.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.comment")}</Label>
                <Input
                  type="select"
                  value={hasCommentFilter}
                  onChange={(e) => setHasCommentFilter(e.target.value)}
                >
                  <option value="">{t("studio.reviews.filters.allComments")}</option>
                  <option value="1">{t("studio.reviews.filters.withComment")}</option>
                  <option value="0">{t("studio.reviews.filters.withoutComment")}</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.reply")}</Label>
                <Input
                  type="select"
                  value={hasReplyFilter}
                  onChange={(e) => setHasReplyFilter(e.target.value)}
                >
                  <option value="">{t("studio.reviews.filters.allReplies")}</option>
                  <option value="0">{t("studio.reviews.filters.withoutReply")}</option>
                  <option value="1">{t("studio.reviews.filters.withReply")}</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.from")}</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Col>
              <Col md={2}>
                <Label className="form-label">{t("studio.reviews.filters.to")}</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted mb-1">{t("studio.reviews.empty.title")}</p>
                <p className="text-muted small mb-0">
                  {hasReplyFilter === "0"
                    ? t("studio.reviews.empty.noPendingReply")
                    : t("studio.reviews.empty.hint")}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("studio.reviews.columns.rating")}</th>
                      <th>{t("studio.reviews.columns.client")}</th>
                      <th>{t("studio.reviews.columns.professional")}</th>
                      <th>{t("studio.reviews.columns.service")}</th>
                      <th>{t("studio.reviews.columns.comment")}</th>
                      <th>{t("studio.reviews.columns.reply")}</th>
                      <th>{t("studio.reviews.columns.date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((review) => (
                      <tr key={review.id}>
                        <td>{renderStars(review.rating)}</td>
                        <td>{review.client?.name ?? "—"}</td>
                        <td>{review.professional?.name ?? "—"}</td>
                        <td>{review.service?.name ?? "—"}</td>
                        <td style={{ maxWidth: 220, whiteSpace: "normal" }}>
                          {review.comment?.trim()
                            ? review.comment
                            : <span className="text-muted">{t("studio.reviews.noComment")}</span>}
                        </td>
                        <td style={{ minWidth: 220, whiteSpace: "normal" }}>
                          {review.reply?.trim() ? (
                            <div>
                              <p className="mb-1 small">{review.reply}</p>
                              {review.replied_by?.name ? (
                                <span className="text-muted small">
                                  {t("studio.reviews.reply.by", { name: review.replied_by.name })}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              <Input
                                type="textarea"
                                rows={2}
                                maxLength={500}
                                value={replyDrafts[review.id] ?? ""}
                                placeholder={t("studio.reviews.reply.placeholder")}
                                onChange={(e) =>
                                  setReplyDrafts((current) => ({
                                    ...current,
                                    [review.id]: e.target.value,
                                  }))
                                }
                              />
                              <Button
                                color="primary"
                                size="sm"
                                className="align-self-start"
                                disabled={replyingId === review.id}
                                onClick={() => void handleReply(review)}
                              >
                                {replyingId === review.id
                                  ? t("studio.reviews.reply.sending")
                                  : t("studio.reviews.reply.submit")}
                              </Button>
                            </div>
                          )}
                        </td>
                        <td>
                          {review.submitted_at
                            ? new Date(review.submitted_at).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ReviewsPage;
